import React, { useState, useEffect } from 'react';
import {
  Users, Calendar, Activity, Search, Bell,
  FileText, Settings, LogOut, ChevronRight,
  Stethoscope, Microscope, Camera, Phone, Mail, ClipboardList,
  Clock, CheckCircle, AlertCircle, UserCheck, HeartPulse, UserPlus, Shield, Building2, List, Edit, Server, Save
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import HostDashboard from './HostDashboard';
import ImagingWorklistPage from './ImagingWorklistPage'; // Importar la nueva página

// --- MOCK DATA FOR DOCTOR DASHBOARD ---
const doctorStatsData = [
  { label: 'Citas Hoy', value: '12', icon: Calendar, color: 'bg-blue-500' },
  { label: 'En Sala de Espera', value: '4', icon: Users, color: 'bg-yellow-500' },
  { label: 'Resultados Críticos', value: '2', icon: Activity, color: 'bg-red-500' },
];
const doctorAppointmentsData = [
  { id: 1, time: '09:00', patient: 'Juan Pérez', type: 'Consulta General', status: 'Finalizado', statusColor: 'text-green-600 bg-green-100' },
  { id: 2, time: '09:30', patient: 'María García', type: 'Seguimiento', status: 'En Consulta', statusColor: 'text-blue-600 bg-blue-100' },
  { id: 3, time: '10:00', patient: 'Carlos López', type: 'Revisión Exámenes', status: 'En Espera', statusColor: 'text-yellow-600 bg-yellow-100' },
  { id: 4, time: '10:30', patient: 'Ana Torres', type: 'Primera Vez', status: 'Confirmado', statusColor: 'text-gray-600 bg-gray-100' },
];
const doctorLabResultsData = [
  { id: 101, patient: 'Carlos López', test: 'Hemograma Completo', result: 'Anormal', time: 'Hace 10 min' },
  { id: 102, patient: 'Luisa Fernández', test: 'Química Sanguínea', result: 'Normal', time: 'Hace 45 min' },
];

// --- MOCK DATA FOR RECEPTION DASHBOARD ---
const initialWaitingPatients = [
    { id: 1, name: 'Ana Torres', arrivalTime: new Date(Date.now() - 5 * 60000), triage: 'normal' },
    { id: 2, name: 'Carlos López', arrivalTime: new Date(Date.now() - 15 * 60000), triage: 'normal' },
    { id: 3, name: 'Sofía Méndez', arrivalTime: new Date(Date.now() - 8 * 60000), triage: 'alto' },
    { id: 4, name: 'Jorge Ruiz', arrivalTime: new Date(Date.now() - 25 * 60000), triage: 'normal' },
];
const triageOrder = { alto: 1, normal: 2 };

// --- HELPER COMPONENTS ---
const SidebarItem = ({ icon: Icon, text, active, onClick, to = "#" }) => (
    <li onClick={onClick}>
        <Link to={to} className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${active ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}>
            <Icon size={20} className="mr-3" />
            <span className="font-medium">{text}</span>
        </Link>
    </li>
);

const StatCard = ({ label, value, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100 flex items-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
        <div className={`p-3 rounded-full ${color} bg-opacity-10 mr-4`}>
            <Icon size={24} className={color.replace('bg-', 'text-')} />
        </div>
        <div>
            <p className="text-gray-500 text-sm">{label}</p>
            <h3 className="text-2xl font-bold text-gray-800">{value}</h3>
        </div>
    </div>
);

// --- DOCTOR DASHBOARD COMPONENT ---
const DoctorDashboard = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <>
            {/* --- SIDEBAR (Doctor) --- */}
            <aside className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col">
                <div className="p-6 flex items-center border-b border-gray-100">
                    <Activity className="text-blue-600 mr-2" />
                    <span className="text-xl font-bold text-gray-800">Tu<span className="text-blue-600">HIS</span></span>
                </div>
                <nav className="flex-1 p-4 overflow-y-auto">
                    <ul className="space-y-2">
                    <SidebarItem icon={Calendar} text="Dashboard" active={true} to="/dashboard" />
                    <SidebarItem icon={Users} text="Pacientes" to="/patients" active={false} />
                    <SidebarItem icon={FileText} text="Consulta Externa" to="/consultas" active={false} />
                    <SidebarItem icon={Activity} text="Laboratorio" to="/labs" active={false} />
                    <SidebarItem icon={Settings} text="Configuración" to="/settings" active={false} />
                </ul>
                </nav>
                <div className="p-4 border-t border-gray-100">
                    <button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-red-600 transition-colors w-full p-2">
                        <LogOut size={20} className="mr-3" />
                        <span>Cerrar Sesión</span>
                    </button>
                </div>
            </aside>

            {/* --- MAIN AREA (Doctor) --- */}
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
                    <div className="flex-1 max-w-xl">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                            <input type="text" placeholder="Buscar paciente por nombre, ID o cédula..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" />
                        </div>
                    </div>
                    <div className="flex items-center space-x-4">
                        <button className="relative p-2 text-gray-400 hover:text-gray-600">
                            <Bell size={20} />
                            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
                        </button>
                        <div className="flex items-center space-x-2">
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold"><HeartPulse size={18} /></div>
                            <span className="text-sm font-medium text-gray-700">{user.name} | Clínica: {user.clinicId}</span>
                        </div>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-8">
                    <div className="mb-8">
                        <h1 className="text-2xl font-bold text-gray-800">Buenos días, Dr. Alejandro</h1>
                        <p className="text-gray-500">Aquí está el resumen de su actividad para hoy.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        {doctorStatsData.map((stat, index) => (
                            <StatCard key={index} {...stat} />
                        ))}
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-2 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-lg font-bold text-gray-800">Agenda del Día</h2>
                                <button className="text-blue-600 text-sm font-medium hover:underline">Ver calendario completo</button>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                        <tr>
                                            <th className="px-6 py-3 font-medium">Hora</th>
                                            <th className="px-6 py-3 font-medium">Paciente</th>
                                            <th className="px-6 py-3 font-medium">Motivo</th>
                                            <th className="px-6 py-3 font-medium">Estado</th>
                                            <th className="px-6 py-3 font-medium">Acción</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {doctorAppointmentsData.map((apt) => (
                                            <tr key={apt.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 text-gray-800 font-medium flex items-center"><Clock size={16} className="mr-2 text-gray-400" />{apt.time}</td>
                                                <td className="px-6 py-4 text-gray-700">{apt.patient}</td>
                                                <td className="px-6 py-4 text-gray-500 text-sm">{apt.type}</td>
                                                <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${apt.statusColor}`}>{apt.status}</span></td>
                                                <td className="px-6 py-4"><Link to={`/atender/${apt.id}`} className="text-blue-600 hover:text-blue-800 font-medium text-sm">Atender</Link></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 flex flex-col">
                            <div className="p-6 border-b border-gray-100"><h2 className="text-lg font-bold text-gray-800 flex items-center"><Activity size={20} className="mr-2 text-blue-500" />Resultados Recientes</h2></div>
                            <div className="p-4 flex-1">
                                <div className="space-y-4">
                                    {doctorLabResultsData.map((res) => (
                                        <div key={res.id} className="flex items-start p-3 bg-gray-50 rounded-lg border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                                            <div className="mt-1 mr-3">{res.result === 'Anormal' ? <AlertCircle className="text-red-500" size={20} /> : <CheckCircle className="text-green-500" size={20} />}</div>
                                            <div>
                                                <p className="text-sm font-bold text-gray-800">{res.patient}</p>
                                                <p className="text-xs text-gray-600">{res.test}</p>
                                                <div className="flex items-center mt-1">
                                                    <span className={`text-xs font-bold ${res.result === 'Anormal' ? 'text-red-600' : 'text-green-600'}`}>{res.result}</span>
                                                    <span className="text-xs text-gray-400 ml-2">• {res.time}</span>
                                                </div>
                                            </div>
                                            <ChevronRight size={16} className="ml-auto text-gray-400 self-center" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="p-4 border-t border-gray-100 text-center"><button className="text-blue-600 text-sm font-medium hover:underline">Ver todos los resultados</button></div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

// --- RECEPTION DASHBOARD COMPONENT ---
const ReceptionDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
      logout();
      navigate('/login');
  };

  const [activeView, setActiveView] = useState('registro'); // registro, agenda, porAtender
  const [serviceType, setServiceType] = useState('consulta'); // 'consulta', 'laboratorio', 'imagenologia'
  const [patientData, setPatientData] = useState({
    curp: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    nombres: '',
    fechaNacimiento: '',
    telefono: '',
    correoElectronico: '',
    sintomas: '',
    peso: '',
    talla: '',
    presion: '',
    triage: 'normal',
    medicoSolicitanteNombre: '',
    medicoSolicitanteTelefono: '',
    medicoSolicitanteCorreo: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPatientData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Aquí iría la lógica para enviar los datos al backend
    console.log("Datos de registro:", patientData, "Tipo de servicio:", serviceType);
    alert("Paciente registrado (simulado)!");
    // Resetear formulario
    setPatientData({
      curp: '',
      apellidoPaterno: '',
      apellidoMaterno: '',
      nombres: '',
      fechaNacimiento: '',
      telefono: '',
      correoElectronico: '',
      sintomas: '',
      peso: '',
      talla: '',
      presion: '',
      triage: 'normal',
      medicoSolicitanteNombre: '',
      medicoSolicitanteTelefono: '',
      medicoSolicitanteCorreo: '',
    });
  };

    const RegistrationForm = () => (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
            <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Tipo de Servicio</h2>
                <div className="flex space-x-4">
                    <button type="button" onClick={() => setServiceType('consulta')} className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${serviceType === 'consulta' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}><Stethoscope size={20} className="mr-2" /> Consulta</button>
                    <button type="button" onClick={() => setServiceType('laboratorio')} className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${serviceType === 'laboratorio' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}><Microscope size={20} className="mr-2" /> Laboratorio</button>
                    <button type="button" onClick={() => setServiceType('imagenologia')} className={`flex items-center px-6 py-3 rounded-lg font-medium transition-colors ${serviceType === 'imagenologia' ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}><Camera size={20} className="mr-2" /> Imagenología</button>
                </div>
            </div>
            <form onSubmit={handleSubmit}>
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Datos del Paciente</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <div><label htmlFor="curp" className="block text-sm font-medium text-gray-700 mb-1">CURP <span className="text-red-500">*</span></label><input type="text" id="curp" name="curp" value={patientData.curp} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="nombres" className="block text-sm font-medium text-gray-700 mb-1">Nombres <span className="text-red-500">*</span></label><input type="text" id="nombres" name="nombres" value={patientData.nombres} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="apellidoPaterno" className="block text-sm font-medium text-gray-700 mb-1">Apellido Paterno <span className="text-red-500">*</span></label><input type="text" id="apellidoPaterno" name="apellidoPaterno" value={patientData.apellidoPaterno} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="apellidoMaterno" className="block text-sm font-medium text-gray-700 mb-1">Apellido Materno <span className="text-red-500">*</span></label><input type="text" id="apellidoMaterno" name="apellidoMaterno" value={patientData.apellidoMaterno} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="fechaNacimiento" className="block text-sm font-medium text-gray-700 mb-1">Fecha de Nacimiento <span className="text-red-500">*</span></label><input type="date" id="fechaNacimiento" name="fechaNacimiento" value={patientData.fechaNacimiento} onChange={handleChange} required className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div><label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label><input type="tel" id="telefono" name="telefono" value={patientData.telefono} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                    <div className="lg:col-span-3"><label htmlFor="correoElectronico" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label><input type="email" id="correoElectronico" name="correoElectronico" value={patientData.correoElectronico} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                </div>
                {serviceType === 'consulta' && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Datos de Consulta</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <div><label htmlFor="peso" className="block text-sm font-medium text-gray-700 mb-1">Peso (kg)</label><input type="number" id="peso" name="peso" value={patientData.peso} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                            <div><label htmlFor="talla" className="block text-sm font-medium text-gray-700 mb-1">Talla (cm)</label><input type="number" id="talla" name="talla" value={patientData.talla} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                            <div><label htmlFor="presion" className="block text-sm font-medium text-gray-700 mb-1">Presión Arterial</label><input type="text" id="presion" name="presion" value={patientData.presion} onChange={handleChange} placeholder="Ej: 120/80" className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                            <div><label htmlFor="triage" className="block text-sm font-medium text-gray-700 mb-1">Triage Médico</label><select id="triage" name="triage" value={patientData.triage} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"><option value="normal">Normal</option><option value="alto">Urgencia / Alto</option></select></div>
                            <div className="md:col-span-4"><label htmlFor="sintomas" className="block text-sm font-medium text-gray-700 mb-1">Síntomas</label><textarea id="sintomas" name="sintomas" value={patientData.sintomas} onChange={handleChange} rows="3" className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Describa los síntomas del paciente..."></textarea></div>
                        </div>
                    </div>
                )}
                {(serviceType === 'laboratorio' || serviceType === 'imagenologia') && (
                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Datos del Médico Solicitante</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div><label htmlFor="medicoSolicitanteNombre" className="block text-sm font-medium text-gray-700 mb-1">Nombre</label><input type="text" id="medicoSolicitanteNombre" name="medicoSolicitanteNombre" value={patientData.medicoSolicitanteNombre} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                            <div><label htmlFor="medicoSolicitanteTelefono" className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label><input type="tel" id="medicoSolicitanteTelefono" name="medicoSolicitanteTelefono" value={patientData.medicoSolicitanteTelefono} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                            <div><label htmlFor="medicoSolicitanteCorreo" className="block text-sm font-medium text-gray-700 mb-1">Correo Electrónico</label><input type="email" id="medicoSolicitanteCorreo" name="medicoSolicitanteCorreo" value={patientData.medicoSolicitanteCorreo} onChange={handleChange} className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" /></div>
                        </div>
                    </div>
                )}
                <div className="mt-8 pt-6 border-t flex justify-end"><button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-md">Registrar Paciente</button></div>
            </form>
        </div>
    );

    const AgendaView = () => (
        <div>
            <div className="mb-8 bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-4">Agendar Cita</h2>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-1"><h3 className="text-lg font-semibold text-gray-700 mb-2">1. Seleccione Fecha y Médico</h3><div className="p-4 border rounded-lg bg-gray-50 text-center text-gray-400">Componente de Calendario (Próximamente)</div><div className="mt-4"><label htmlFor="doctor" className="block text-sm font-medium text-gray-700 mb-1">Médico en Turno</label><select id="doctor" name="doctor" className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"><option>Dr. Alejandro Magno</option><option>Dra. Isabela Castillo</option></select></div></div>
                    <div className="lg:col-span-2"><h3 className="text-lg font-semibold text-gray-700 mb-2">2. Complete los Datos del Paciente</h3><RegistrationForm /></div>
                </div>
            </div>
        </div>
    );

    const PorAtenderView = () => {
        const [currentTime, setCurrentTime] = useState(new Date());
        useEffect(() => {
            const timer = setInterval(() => setCurrentTime(new Date()), 60000); // Actualiza cada minuto
            return () => clearInterval(timer);
        }, []);

        const sortedPatients = [...initialWaitingPatients].sort((a, b) => (triageOrder[a.triage] - triageOrder[b.triage]) || (a.arrivalTime - b.arrivalTime));

        return (
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-6 border-b"><h2 className="text-xl font-semibold text-gray-800">Pacientes por Atender</h2></div>
                <table className="w-full text-left">
                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase"><tr><th className="px-6 py-3 font-medium">Paciente</th><th className="px-6 py-3 font-medium">Triage</th><th className="px-6 py-3 font-medium">Tiempo de Espera</th><th className="px-6 py-3 font-medium">Acción</th></tr></thead>
                    <tbody className="divide-y divide-gray-100">
                        {sortedPatients.map(p => {
                            const waitingMinutes = Math.round((currentTime - p.arrivalTime) / 60000);
                            return (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 font-medium text-gray-800">{p.name}</td>
                                    <td className="px-6 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium ${p.triage === 'alto' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{p.triage === 'alto' ? 'Urgencia' : 'Normal'}</span></td>
                                    <td className="px-6 py-4 text-gray-600">{waitingMinutes} minutos</td>
                                    <td className="px-6 py-4"><button className="text-blue-600 hover:text-blue-800 font-medium text-sm">Ver Detalles</button></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <>
            <aside className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col">
                <div className="p-6 flex items-center border-b border-gray-100"><Activity className="text-blue-600 mr-2" /><span className="text-xl font-bold text-gray-800">Tu<span className="text-blue-600">HIS</span></span></div>
                <nav className="flex-1 p-4 overflow-y-auto"><ul className="space-y-2">
                    <SidebarItem icon={ClipboardList} text="Registro Pacientes" active={activeView === 'registro'} onClick={() => setActiveView('registro')} />
                    <SidebarItem icon={Calendar} text="Agenda Citas" active={activeView === 'agenda'} onClick={() => setActiveView('agenda')} />
                    <SidebarItem icon={Users} text="Por Atender" active={activeView === 'porAtender'} onClick={() => setActiveView('porAtender')} />
                </ul></nav>
                <div className="p-4 border-t border-gray-100"><button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-red-600 transition-colors w-full p-2"><LogOut size={20} className="mr-3" /><span>Cerrar Sesión</span></button></div>
            </aside>
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
                    <div className="flex-1 max-w-xl"><div className="relative"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} /><input type="text" placeholder="Buscar paciente por nombre, ID o cédula..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" /></div></div>
                    <div className="flex items-center space-x-4">
                        <button className="relative p-2 text-gray-400 hover:text-gray-600"><Bell size={20} /><span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span></button>
                        <div className="flex items-center space-x-2"><div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold"><UserCheck size={18} /></div><span className="text-sm font-medium text-gray-700">{user.name} | Clínica: {user.clinicId}</span></div>
                    </div>
                </header>
                <div className="flex-1 overflow-y-auto p-8">
                    {activeView === 'registro' && <RegistrationForm />}
                    {activeView === 'agenda' && <AgendaView />}
                    {activeView === 'porAtender' && <PorAtenderView />}
                </div>
            </main>
        </>
    );
};

// --- ADMIN DASHBOARD COMPONENT ---
const AdminDashboard = () => {
    const { user, logout, sites, users: allUsers, pacsConfig, updatePacsConfig } = useAuth();
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState('users'); // 'users', 'sites', 'settings', etc.

    const allRoles = ['recepcion', 'enfermeria', 'medico', 'farmacia', 'laboratorio', 'imagenologia', 'host', 'administracion'];
    const cedulaRoles = ['enfermeria', 'medico', 'farmacia', 'laboratorio', 'imagenologia'];

    const [newUser, setNewUser] = useState({
        curp: '', apellidoPaterno: '', apellidoMaterno: '', nombres: '',
        titulo: '', id: '', cedulaProfesional: '', cedulaEnTramite: false,
        roles: [],
        clinicId: ''
    });

    const [newSite, setNewSite] = useState({
        nombre: '', calle: '', numero: '', colonia: '',
        municipio: '', estado: '', codigoPostal: '',
        responsable: '', telefono: ''
    });

    const [currentPacsConfig, setCurrentPacsConfig] = useState(pacsConfig);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewUser(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleRoleChange = (e) => {
        const { value, checked } = e.target;
        setNewUser(prev => ({
            ...prev,
            roles: checked ? [...prev.roles, value] : prev.roles.filter(role => role !== value)
        }));
    };

    const showCedulaField = newUser.roles.some(role => cedulaRoles.includes(role));

    const handleSiteInputChange = (e) => {
        const { name, value } = e.target;
        setNewSite(prev => ({ ...prev, [name]: value }));
    };

    const handlePacsConfigChange = (e) => {
        const { name, value } = e.target;
        setCurrentPacsConfig(prev => ({ ...prev, [name]: value }));
    };

    const handleLogout = () => {
        logout();
        navigate('/login');

    };

    const handleSubmit = (e) => {
        e.preventDefault();
        console.log("Creando nuevo usuario:", newUser);
        alert("Usuario creado (simulado). Ver la consola para más detalles.");
        // Aquí iría la lógica para resetear el formulario
    };

    const handleSiteSubmit = (e) => {
        e.preventDefault();
        console.log("Creando nuevo sitio:", newSite);
        alert("Sitio creado (simulado). Ver la consola para más detalles.");
        // Lógica para resetear el formulario
    };

    const handlePacsConfigSubmit = (e) => {
        e.preventDefault();
        updatePacsConfig(currentPacsConfig);
        alert("Configuración del PACS guardada.");
    };

    return (
        <>
            <aside className="w-64 bg-white border-r border-gray-200 hidden md:flex flex-col">
                <div className="p-6 flex items-center border-b border-gray-100"><Shield className="text-blue-600 mr-2" /><span className="text-xl font-bold text-gray-800">Tu<span className="text-blue-600">HIS</span> Admin</span></div>
                <nav className="flex-1 p-4 overflow-y-auto"><ul className="space-y-2">
                    <SidebarItem icon={UserPlus} text="Alta de Usuarios" active={activeView === 'users'} onClick={() => setActiveView('users')} />
                    <SidebarItem icon={List} text="Mant. de Usuarios" active={activeView === 'listUsers'} onClick={() => setActiveView('listUsers')} />
                    <SidebarItem icon={Building2} text="Alta de Sitios" active={activeView === 'sites'} onClick={() => setActiveView('sites')} />
                    <SidebarItem icon={List} text="Mant. de Sitios" active={activeView === 'listSites'} onClick={() => setActiveView('listSites')} />
                    <SidebarItem icon={Settings} text="Configuración" active={false} />
                </ul></nav>
                <div className="p-4 border-t border-gray-100"><button onClick={handleLogout} className="flex items-center text-gray-600 hover:text-red-600 transition-colors w-full p-2"><LogOut size={20} className="mr-3" /><span>Cerrar Sesión</span></button></div>

            </aside>
            <main className="flex-1 flex flex-col overflow-hidden">
                <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
                    <h1 className="text-xl font-bold text-gray-800">
                        {activeView === 'users' ? 'Gestión de Usuarios' : 'Gestión de Sitios'}
                    </h1>
                    <div className="flex items-center space-x-2"><div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold"><Shield size={18} /></div><span className="text-sm font-medium text-gray-700">{user.name} | Clínica: {user.clinicId}</span></div>
                </header>
                <div className="flex-1 overflow-y-auto p-8">
                    {activeView === 'users' && (

                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                            <form onSubmit={handleSubmit}>
                                <h2 className="text-2xl font-bold text-gray-800 mb-6">Crear Nuevo Usuario</h2>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                    <div><label htmlFor="curp" className="block text-sm font-medium text-gray-700 mb-1">CURP <span className="text-red-500">*</span></label><input type="text" name="curp" value={newUser.curp} onChange={handleInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="nombres" className="block text-sm font-medium text-gray-700 mb-1">Nombres <span className="text-red-500">*</span></label><input type="text" name="nombres" value={newUser.nombres} onChange={handleInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="apellidoPaterno" className="block text-sm font-medium text-gray-700 mb-1">Apellido Paterno <span className="text-red-500">*</span></label><input type="text" name="apellidoPaterno" value={newUser.apellidoPaterno} onChange={handleInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="apellidoMaterno" className="block text-sm font-medium text-gray-700 mb-1">Apellido Materno <span className="text-red-500">*</span></label><input type="text" name="apellidoMaterno" value={newUser.apellidoMaterno} onChange={handleInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="titulo" className="block text-sm font-medium text-gray-700 mb-1">Título (Ej. Dr., Lic., Enf.)</label><input type="text" name="titulo" value={newUser.titulo} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-1">ID de Usuario (Login) <span className="text-red-500">*</span></label><input type="text" name="id" value={newUser.id} onChange={handleInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                </div>

                            <div className="mb-6">
                                <label htmlFor="clinicId" className="block text-sm font-medium text-gray-700 mb-1">Asignar a Sitio/Clínica <span className="text-red-500">*</span></label>
                                <select
                                    name="clinicId"
                                    id="clinicId"
                                    value={newUser.clinicId}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                >
                                    <option value="" disabled>Seleccione una clínica</option>
                                    {sites.map(site => (<option key={site.id} value={site.id}>{site.name}</option>))}
                                </select>
                            </div>

                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Roles de Usuario</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {allRoles.map(role => (
                                            <label key={role} className="flex items-center space-x-2 p-2 border rounded-lg hover:bg-gray-50">
                                                <input type="checkbox" value={role} checked={newUser.roles.includes(role)} onChange={handleRoleChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                                                <span className="capitalize">{role.replace('_', ' ')}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {showCedulaField && (
                                    <div className="p-4 border-l-4 border-blue-500 bg-blue-50 rounded-lg mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-3">Información Profesional</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            <div>
                                                <label htmlFor="cedulaProfesional" className="block text-sm font-medium text-gray-700 mb-1">Cédula Profesional</label>
                                                <input type="text" name="cedulaProfesional" value={newUser.cedulaProfesional} onChange={handleInputChange} disabled={newUser.cedulaEnTramite} className="w-full p-3 border border-gray-300 rounded-lg disabled:bg-gray-200" />
                                                <label className="flex items-center space-x-2 mt-2">
                                                    <input type="checkbox" name="cedulaEnTramite" checked={newUser.cedulaEnTramite} onChange={handleInputChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                                                    <span>Cédula en trámite (con documento acreditativo)</span>
                                                </label>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Subir Cédula (PDF/Imagen)</label>
                                                <input type="file" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"/>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Foto del Usuario (Opcional)</label>
                                    <input type="file" accept="image/*" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"/>
                                </div>

                                <div className="mt-8 pt-6 border-t flex justify-end">
                                    <button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-md flex items-center">
                                        <UserPlus size={20} className="mr-2" /> Crear Usuario
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                      {activeView === 'listUsers' && (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Usuarios Registrados</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                        <tr>
                                            <th className="px-6 py-3 font-medium">ID de Usuario</th>
                                            <th className="px-6 py-3 font-medium">Nombre</th>
                                            <th className="px-6 py-3 font-medium">Clínica</th>
                                            <th className="px-6 py-3 font-medium">Roles</th>
                                            <th className="px-6 py-3 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {allUsers.map((u) => (
                                            <tr key={u.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-mono text-sm">{u.username}</td>
                                                <td className="px-6 py-4 font-medium text-gray-800">{u.name}</td>
                                                <td className="px-6 py-4 text-gray-600">{sites.find(s => s.id === u.clinicId)?.name || 'N/A'}</td>
                                                <td className="px-6 py-4 text-gray-600 capitalize text-xs">{u.roles.join(', ')}</td>
                                                <td className="px-6 py-4">
                                                    <button className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-4"><Edit size={16} className="inline-block mr-1" />Editar</button>
                                                    <button className="text-red-600 hover:text-red-800 font-medium text-sm"><LogOut size={16} className="inline-block mr-1" />Desactivar</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    {activeView === 'listSites' && (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6 p-6">Sitios Registrados</h2>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
                                        <tr>
                                            <th className="px-6 py-3 font-medium">ID del Sitio</th>
                                            <th className="px-6 py-3 font-medium">Nombre de la Clínica</th>
                                            <th className="px-6 py-3 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {sites.map((site) => (
                                            <tr key={site.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 font-mono text-sm">{site.id}</td>
                                                <td className="px-6 py-4 font-medium text-gray-800">{site.name}</td>
                                                <td className="px-6 py-4">
                                                    <button className="text-blue-600 hover:text-blue-800 font-medium text-sm mr-4"><Edit size={16} className="inline-block mr-1" />Editar</button>
                                                    <button className="text-red-600 hover:text-red-800 font-medium text-sm"><LogOut size={16} className="inline-block mr-1" />Desactivar</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}




                    {activeView === 'sites' && (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8">
                            <form onSubmit={handleSiteSubmit}>
                                <h2 className="text-2xl font-bold text-gray-800 mb-6">Crear Nuevo Sitio (Clínica)</h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                    <div className="md:col-span-2"><label htmlFor="nombre" className="block text-sm font-medium text-gray-700 mb-1">Nombre de la Clínica <span className="text-red-500">*</span></label><input type="text" name="nombre" value={newSite.nombre} onChange={handleSiteInputChange} required className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="calle" className="block text-sm font-medium text-gray-700 mb-1">Calle</label><input type="text" name="calle" value={newSite.calle} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="numero" className="block text-sm font-medium text-gray-700 mb-1">Número</label><input type="text" name="numero" value={newSite.numero} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="colonia" className="block text-sm font-medium text-gray-700 mb-1">Colonia</label><input type="text" name="colonia" value={newSite.colonia} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="municipio" className="block text-sm font-medium text-gray-700 mb-1">Municipio</label><input type="text" name="municipio" value={newSite.municipio} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">Estado</label><input type="text" name="estado" value={newSite.estado} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="codigoPostal" className="block text-sm font-medium text-gray-700 mb-1">Código Postal</label><input type="text" name="codigoPostal" value={newSite.codigoPostal} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div className="md:col-span-2"><label htmlFor="responsable" className="block text-sm font-medium text-gray-700 mb-1">Responsable</label><input type="text" name="responsable" value={newSite.responsable} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">Teléfono</label><input type="tel" name="telefono" value={newSite.telefono} onChange={handleSiteInputChange} className="w-full p-3 border border-gray-300 rounded-lg" /></div>
                                    <div><label className="block text-sm font-medium text-gray-700 mb-1">Logo de la Clínica</label><input type="file" accept="image/*" className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"/></div>
                                </div>
                                <div className="mt-8 pt-6 border-t flex justify-end">
                                    <button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-md flex items-center">
                                        <Building2 size={20} className="mr-2" /> Crear Sitio
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                    {activeView === 'settings' && (
                        <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-8 max-w-2xl mx-auto">
                            <form onSubmit={handlePacsConfigSubmit}>
                                <h2 className="text-2xl font-bold text-gray-800 mb-6">Parámetros de Conexión PACS</h2>
                                <div className="space-y-6">
                                    <div>
                                        <label htmlFor="ip" className="block text-sm font-medium text-gray-700 mb-1">Dirección IP del Servidor</label>
                                        <input type="text" name="ip" value={currentPacsConfig.ip} onChange={handlePacsConfigChange} className="w-full p-3 border border-gray-300 rounded-lg" />
                                    </div>
                                    <div>
                                        <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-1">Puerto DICOM</label>
                                        <input type="number" name="port" value={currentPacsConfig.port} onChange={handlePacsConfigChange} className="w-full p-3 border border-gray-300 rounded-lg" />
                                    </div>
                                    <div>
                                        <label htmlFor="aeTitle" className="block text-sm font-medium text-gray-700 mb-1">AE Title del Servidor</label>
                                        <input type="text" name="aeTitle" value={currentPacsConfig.aeTitle} onChange={handlePacsConfigChange} className="w-full p-3 border border-gray-300 rounded-lg" />
                                    </div>
                                </div>
                                <div className="mt-8 pt-6 border-t flex justify-end"><button type="submit" className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold text-lg hover:bg-blue-700 transition-colors shadow-md flex items-center"><Save size={20} className="mr-2" /> Guardar Cambios</button></div>
                            </form>
                        </div>
                    )}
                </div>
            </main>
        </>
    );
};

// --- MAIN DASHBOARD COMPONENT (THE EXPORTED ONE) ---
const Dashboard = () => {
    const { user, isAuthenticated } = useAuth();
    let dashboardContent;

    // Si no está autenticado, ProtectedRoute ya lo habrá redirigido, pero es una buena práctica verificar.
    if (!isAuthenticated) {
        return null; 
    }

    // Lógica de prioridad de roles para mostrar el dashboard adecuado
    if (user.roles.includes('administracion')) {
        dashboardContent = <AdminDashboard />;
    } else if (user.roles.includes('medico')) {
        dashboardContent = <DoctorDashboard />;
    } else if (user.roles.includes('recepcion')) {
        dashboardContent = <ReceptionDashboard />;
    } else if (user.roles.includes('imagenologia')) {
        dashboardContent = <ImagingWorklistPage />;
    } else if (user.roles.includes('host')) {
        dashboardContent = <HostDashboard />;
    } else {
        // Fallback para roles aún no implementados
        dashboardContent = <div>Dashboard para el rol: {user.roles.join(', ')}</div>;
    }

    return (
        <div className="flex h-screen bg-slate-100 font-sans">
            {dashboardContent}
        </div>
    );
};

export default Dashboard;